import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { Document, Page, pdfjs } from 'react-pdf'
import { useAppContext } from 'contexts/app-context/hooks'
import { GerarLinkRelatorioRequest, useGenerateReportBR } from 'hooks/useGenerateReportBR'
import { useGenerateReportUS, PostReportRequest } from 'hooks/useGenerateReportUS'
import { SCREEN } from 'components/pdf-viewer/utils/constants'
import { REPORT_DICTIONARY } from 'common/types/br/Response.types'
import { TReportType } from 'contexts/app-context/types'

import { Text } from '@interco/inter-ui/components/Text'
import { Loading } from '@interco/inter-ui/components/Loading'

import { PdfContainer, ReportContainerUs } from './styles'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export const ReportViwer: FC = () => {
  const { userCountry, userLanguage } = useAppContext()

  const { state } = useLocation()

  const navigate = useNavigate()

  const [totalPages, setTotalPages] = useState<number | undefined>()

  const reportDataBR: GerarLinkRelatorioRequest = {
    trackNumber: state?.trackNumber ?? '',
    dataSolicitacao: state?.requestDate ?? '',
    tipoSolicitacao: REPORT_DICTIONARY[state?.requestType as TReportType] ?? '',
  }

  const reportDataUS: PostReportRequest = {
    trackNumber: state?.trackNumber ?? '',
    dataRequest: state?.requestDate ?? '',
    requestType: state?.requestType ?? '',
  }

  const {
    fetchReport: fetchBR,
    isReportError: isErrorBR,
    isReportLoading: isLoadingBR,
    report: reportBR,
  } = useGenerateReportBR(reportDataBR)

  const {
    fetchReport: fetchUS,
    isReportError: isErrorUS,
    isReportLoading: isLoadingUS,
    report: reportUS,
  } = useGenerateReportUS(reportDataUS)

  const isError = isErrorBR || isErrorUS

  const isLoading = isLoadingBR || isLoadingUS

  const fetchUSReport = useCallback(async () => {
    await fetchUS()
  }, [fetchUS])

  const fetchBRReport = useCallback(async () => {
    await fetchBR()
  }, [fetchBR])

  useEffect(() => {
    if (userCountry === 'US') fetchUSReport()
    else fetchBRReport()
  }, [fetchBRReport, fetchUSReport, userCountry])

  const fetchedReport = useMemo(() => {
    if (reportBR?.exist) return reportBR
    if (reportUS?.exist) return reportUS
    return undefined
  }, [reportBR, reportUS])

  const textContent = useMemo(() => {
    if (userLanguage === 'en-US')
      return {
        loadingMessage: 'Getting report',
        downloadingMessage: 'Downloading file',
        errorMessage: 'Something went wrong trying to get the file.',
      }

    if (userLanguage === 'es-ES')
      return {
        loadingMessage: 'Obteniendo informe',
        downloadingMessage: 'Descargando archivo',
        errorMessage: 'Algo salió mal al intentar obtener el archivo.',
      }

    return {
      loadingMessage: 'Obtendo relatório',
      downloadingMessage: 'Baixando arquivo',
      errorMessage: 'Algo deu errado ao tentar obter o arquivo.',
    }
  }, [userLanguage])

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setTotalPages(numPages)
  }, [])

  const onDocumentLoadError = useCallback(() => {
    navigate(Routes.UNAVAILABLE_REPORT, { state: { goTo: Routes.REPORTS_HISTORY } })
  }, [navigate])

  if (isLoading)
    return (
      <PdfContainer>
        <Loading width={24} height={24} />
        <Text variant="body-3" as="p" style={{ marginTop: '8px' }}>
          {textContent.loadingMessage}
        </Text>
      </PdfContainer>
    )

  if (isError)
    return <Navigate to={Routes.UNAVAILABLE_REPORT} state={{ goTo: Routes.REPORTS_HISTORY }} />

  if (!fetchedReport) return null

  // us html report
  if (userCountry === 'US') return <ReportContainerUs src={fetchedReport.url} title="Report" />

  // br pdf report
  return (
    <PdfContainer>
      <Document
        onPassword={(cbb) => cbb(fetchedReport?.socialId.slice(-4))}
        file={fetchedReport}
        error={textContent.errorMessage}
        loading={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Loading width={24} height={24} />
            <Text variant="body-3" as="p" color="typography" style={{ marginTop: '8px' }}>
              {textContent.downloadingMessage}
            </Text>
          </div>
        }
        className="pdf__container"
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
      >
        {Array.from(new Array(totalPages), (_el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            width={SCREEN.availWidth - 40}
            scale={1.1}
          />
        ))}
      </Document>
    </PdfContainer>
  )
}
